import axios from 'axios';

const AI_PROVIDERS = {
  OPENAI: 'openai',
  GEMINI: 'gemini',
  ANTHROPIC: 'anthropic',
  PERPLEXITY: 'perplexity'
};

const getActiveProvider = () => {
  if (import.meta.env?.VITE_OPENAI_API_KEY && import.meta.env?.VITE_OPENAI_API_KEY !== 'your-openai-api-key-here') {
    return AI_PROVIDERS?.OPENAI;
  }
  if (import.meta.env?.VITE_GEMINI_API_KEY && import.meta.env?.VITE_GEMINI_API_KEY !== 'your-gemini-api-key-here') {
    return AI_PROVIDERS?.GEMINI;
  }
  if (import.meta.env?.VITE_ANTHROPIC_API_KEY && import.meta.env?.VITE_ANTHROPIC_API_KEY !== 'your-anthropic-api-key-here') {
    return AI_PROVIDERS?.ANTHROPIC;
  }
  if (import.meta.env?.VITE_PERPLEXITY_API_KEY && import.meta.env?.VITE_PERPLEXITY_API_KEY !== 'your-perplexity-api-key-here') {
    return AI_PROVIDERS?.PERPLEXITY;
  }
  return null;
};

const callOpenAI = async (prompt) => {
  const response = await axios?.post(
    'https://api.openai.com/v1/chat/completions',
    {
      model: 'gpt-4',
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.7
    },
    {
      headers: {
        'Authorization': `Bearer ${import.meta.env?.VITE_OPENAI_API_KEY}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return response?.data?.choices?.[0]?.message?.content;
};

const callGemini = async (prompt) => {
  const response = await axios?.post(
    `https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent?key=${import.meta.env?.VITE_GEMINI_API_KEY}`,
    {
      contents: [{ parts: [{ text: prompt }] }]
    },
    {
      headers: { 'Content-Type': 'application/json' }
    }
  );
  return response?.data?.candidates?.[0]?.content?.parts?.[0]?.text;
};

const callAnthropic = async (prompt) => {
  const response = await axios?.post(
    'https://api.anthropic.com/v1/messages',
    {
      model: 'claude-3-sonnet-20240229',
      max_tokens: 1024,
      messages: [{ role: 'user', content: prompt }]
    },
    {
      headers: {
        'x-api-key': import.meta.env?.VITE_ANTHROPIC_API_KEY,
        'anthropic-version': '2023-06-01',
        'Content-Type': 'application/json'
      }
    }
  );
  return response?.data?.content?.[0]?.text;
};

const callPerplexity = async (prompt) => {
  const response = await axios?.post(
    'https://api.perplexity.ai/chat/completions',
    {
      model: 'llama-3.1-sonar-small-128k-online',
      messages: [{ role: 'user', content: prompt }]
    },
    {
      headers: {
        'Authorization': `Bearer ${import.meta.env?.VITE_PERPLEXITY_API_KEY}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return response?.data?.choices?.[0]?.message?.content;
};

export const generateAIContent = async (prompt) => {
  const provider = getActiveProvider();
  
  if (!provider) {
    throw new Error('No AI service configured. Please add an API key for OpenAI, Gemini, Anthropic, or Perplexity in your .env file.');
  }

  try {
    switch (provider) {
      case AI_PROVIDERS?.OPENAI:
        return await callOpenAI(prompt);
      case AI_PROVIDERS?.GEMINI:
        return await callGemini(prompt);
      case AI_PROVIDERS?.ANTHROPIC:
        return await callAnthropic(prompt);
      case AI_PROVIDERS?.PERPLEXITY:
        return await callPerplexity(prompt);
      default:
        throw new Error('Invalid AI provider');
    }
  } catch (error) {
    console.error('AI generation error:', error);
    throw new Error(`Failed to generate AI content: ${error?.message}`);
  }
};

export const generateListingCopy = async (propertyData, storyAnswers) => {
  const prompt = `Generate compelling real estate listing copy for the following property:

Property Details:
- Type: ${propertyData?.type}
- Bedrooms: ${propertyData?.beds}
- Bathrooms: ${propertyData?.baths}
- Parking: ${propertyData?.parking}
- Address: ${propertyData?.address}

Owner's Story:
- What they love: ${storyAnswers?.whatYouLove}
- Recent renovations: ${storyAnswers?.renovations}
- Ideal buyer/tenant: ${storyAnswers?.idealBuyer}

Please provide:
1. Three compelling headlines (each 10-15 words)
2. A short description (50-75 words)
3. A full description (150-200 words)

Format as JSON with keys: headlines (array), shortDescription (string), fullDescription (string)`;

  const response = await generateAIContent(prompt);
  
  try {
    const jsonMatch = response?.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      return JSON.parse(jsonMatch?.[0]);
    }
  } catch (e) {
    console.error('Failed to parse AI response as JSON:', e);
  }
  
  return {
    headlines: ['Beautiful Property in Prime Location', 'Modern Living at Its Finest', 'Your Dream Home Awaits'],
    shortDescription: 'Discover this exceptional property featuring modern amenities and prime location.',
    fullDescription: 'This stunning property offers the perfect blend of comfort and style. With spacious living areas and premium finishes throughout, it represents an outstanding opportunity for discerning buyers.'
  };
};

export const answerPropertyQuestion = async (listingData, question) => {
  const prompt = `You are a helpful real estate assistant. Answer the following question about this property:

Property: ${listingData?.address}
Type: ${listingData?.type}
Bedrooms: ${listingData?.beds}
Bathrooms: ${listingData?.baths}
Price: ${listingData?.price}
Description: ${listingData?.description}

Question: ${question}

Provide a helpful, concise answer (2-3 sentences).`;

  return await generateAIContent(prompt);
};

export default { generateAIContent, generateListingCopy, answerPropertyQuestion };